import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Plus, Check, X, Search } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { api, errMsg } from "@/lib/api";
import { stems, hitsQuery } from "@/lib/search";

const FIELDS = [
  [
    { k: "car_make", label: "Marka", ph: "BMW" },
    { k: "car_model", label: "Modelis", ph: "330d" },
    { k: "car_year", label: "Gads", ph: "2016" },
    { k: "mileage", label: "Nobraukums", ph: "210 000 km" },
  ],
  [
    { k: "name", label: "Vārds", ph: "Jānis", req: true },
    { k: "phone", label: "Telefons", ph: "2xxxxxxx", req: true },
    { k: "email", label: "E-pasts", ph: "janis@epasts.lv" },
    { k: "preferred_date", label: "Vēlamais datums", ph: "", type: "date" },
  ],
];

const EMPTY = {
  name: "", phone: "", email: "", car_make: "", car_model: "", car_year: "",
  mileage: "", service: "", preferred_date: "", message: "",
};

export const BookingSection = ({ priceCategories = [], picked = [], setPicked }) => {
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const all = useMemo(
    () =>
      priceCategories.flatMap((c) =>
        (c.rows || []).map((r, i) => ({
          key: `${c.id}-${i}`,
          label: r.group ? `${r.group} — ${r.label}` : r.label,
          price: r.price,
          category: c.title,
        }))
      ),
    [priceCategories]
  );

  const tokens = useMemo(() => stems(q.trim()), [q]);
  const options = useMemo(
    () => (tokens.length ? all.filter((o) => hitsQuery(`${o.label} ${o.category}`, tokens)) : all),
    [all, tokens]
  );

  const add = (o) => setPicked((p) => (p.some((x) => x.key === o.key) ? p : [...p, o]));
  const remove = (key) => setPicked((p) => p.filter((x) => x.key !== key));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Lūdzu norādi vārdu un telefonu");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, service: picked.map((p) => p.label).join(", ") };
      const { data } = await api.post("/bookings", payload);
      setDone(data.reference);
      setForm(EMPTY);
      setPicked([]);
      setQ("");
      toast.success(`Pieteikums saņemts! Nr. ${data.reference}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="pieteikums" className="relative border-t border-white/10 py-24 sm:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_30%,rgba(225,29,72,0.14),transparent_55%)]" />
      <div className="relative mx-auto max-w-[1500px] px-5 sm:px-10">
        <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="min-w-0">
            <SectionLabel num="/ 05">Pieteikums</SectionLabel>
            <Reveal>
              <h2 className="mt-5 break-words font-head text-4xl font-extrabold uppercase italic leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl">
                <span className="metal-text">PIETEIC SAVU</span> <span className="crimson-text">AUTO</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400">
                Aizpildi pieteikumu — sazināsimies 24 stundu laikā ar aptuvenu cenu un brīvu laiku darbnīcā.
              </p>
            </Reveal>
            {done && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="booking-success"
                className="mt-8 flex items-center gap-4 border border-rose-600/40 bg-rose-600/10 p-5"
              >
                <CheckCircle2 className="h-6 w-6 shrink-0 text-rose-400" />
                <div>
                  <div className="font-head text-lg font-bold uppercase text-white">Pieteikums nosūtīts</div>
                  <div className="font-mono-t text-xs text-rose-300">Numurs: {done}</div>
                </div>
              </motion.div>
            )}
          </div>

          <Reveal delay={0.12} className="min-w-0">
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="metal-panel relative overflow-hidden p-6 sm:p-9"
            >
              {FIELDS.map((group, gi) => (
                <div key={gi} className={gi > 0 ? "mt-8" : ""}>
                  <div className="mb-4 flex items-center gap-3 font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    <span className="text-rose-500">0{gi + 1}</span>
                    {gi === 0 ? "Auto informācija" : "Kontakti un laiks"}
                    <span className="h-px flex-1 bg-white/10" />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {group.map((f) => (
                      <label key={f.k} className="block">
                        <span className="font-mono-t text-[10px] uppercase tracking-[0.18em] text-slate-500">
                          {f.label}
                          {f.req && <span className="text-rose-500"> *</span>}
                        </span>
                        <input
                          type={f.type || "text"}
                          value={form[f.k]}
                          placeholder={f.ph}
                          onChange={(e) => set(f.k, e.target.value)}
                          data-testid={`booking-input-${f.k}`}
                          className="mt-2 w-full border border-white/10 bg-black/40 px-4 py-3 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-rose-500"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <div className="mt-8">
                <div className="mb-4 flex items-center gap-3 font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  <span className="text-rose-500">03</span> Izvēlies darbus
                  <span className="h-px flex-1 bg-white/10" />
                </div>

                {picked.length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-2" data-testid="booking-picked">
                    {picked.map((p) => (
                      <span
                        key={p.key}
                        data-testid={`booking-picked-${p.key}`}
                        className="inline-flex max-w-full items-center gap-2 break-words border border-rose-600/60 bg-rose-600/15 px-3 py-1.5 text-left font-mono-t text-[10px] uppercase tracking-[0.12em] text-rose-200"
                      >
                        {p.label}
                        <button
                          type="button"
                          onClick={() => remove(p.key)}
                          aria-label="Noņemt"
                          className="text-rose-300 transition-colors hover:text-white"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Meklē darbu no cenrāža — piem. bremzes"
                    data-testid="booking-service-search"
                    className="w-full border border-white/10 bg-black/40 py-3 pl-11 pr-4 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-rose-500"
                  />
                </div>

                <div
                  className="mt-3 max-h-64 overflow-y-auto border border-white/10 bg-black/30"
                  data-testid="booking-service-picker"
                >
                  {options.length === 0 && (
                    <p className="px-4 py-5 font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Nekas netika atrasts — apraksti darbu zemāk
                    </p>
                  )}
                  {options.map((o) => {
                    const chosen = picked.some((p) => p.key === o.key);
                    return (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => (chosen ? remove(o.key) : add(o))}
                        data-testid={`booking-service-${o.key}`}
                        className={`flex w-full items-center gap-3 border-b border-white/[0.06] px-4 py-3 text-left transition-colors last:border-0 ${
                          chosen ? "bg-rose-600/10" : "hover:bg-white/[0.04]"
                        }`}
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center border transition-colors ${
                            chosen ? "border-rose-500 bg-rose-600 text-white" : "border-white/20 text-slate-400"
                          }`}
                        >
                          {chosen ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block break-words text-sm text-slate-200">{o.label}</span>
                          <span className="font-mono-t text-[9px] uppercase tracking-[0.18em] text-slate-600">
                            {o.category}
                          </span>
                        </span>
                        <span className="whitespace-nowrap font-head text-sm font-bold text-white">{o.price}</span>
                      </button>
                    );
                  })}
                </div>

                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="Ja vēlies kaut ko papildus — apraksti šeit..."
                  data-testid="booking-input-message"
                  className="mt-4 w-full resize-none border border-white/10 bg-black/40 px-4 py-3 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-rose-500"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit-btn"
                className="mt-8 flex w-full items-center justify-center gap-3 bg-rose-600 py-4 font-head text-lg font-bold uppercase tracking-wide text-white transition-all duration-300 hover:bg-rose-500 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Nosūtīt pieteikumu
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
